import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import logoBlackText from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import LogoWhite from '../../assets/images/logos/white-bg-logo-hiacuity.png';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import { confirmPasswordReset } from '../../utils/api_service/invite_api';
import { getImageSrc } from '../../utils/helpers/image-utils';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import styles from './login_register.module.css';
import { validate, validateForm } from './validation/validation_rules';

export const ChangePassword = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState<FormInput>({
    email: localStorage.getItem('email') || 'sample@hiacuity.com',
    password: '',
    confirmPassword: '',
    errors: {
      emailError: '',
      passwordError: '',
      confirmPasswordError: '',
    },
  });
  const [formValid, setFormValid] = useState<boolean>(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let errors = validate(event.target, password);

    setPassword({
      ...password,
      [event.target.name]: event.target.value,
      errors: errors,
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let isSubmittedFormValid = validateForm(password);
    if (isSubmittedFormValid) {
      // The emailed reset token is single-use and NOT a session; send it in the
      // request body rather than storing it as the auth token.
      confirmPasswordReset(token, password.password)
        .then((response) => {
          if (response.ok) {
            setFormValid(true);
            history.push('/login');
            toast.success(t(AUTH_KEYS.changePassword.success), {
              position: 'bottom-center',
            });
          } else {
            return response.json().then((data) => {
              toast.error(data.message || 'This reset link is no longer valid', { position: 'bottom-center' });
            });
          }
        })
        .catch((error) => toast.error(error, { position: 'bottom-center' }));
    } else {
      toast.error(t(AUTH_KEYS.login.invalidDetails), {
        position: 'bottom-center',
      });
      setFormValid(false);
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout
        leftContent={
          <div className='flex flex-col items-center gap-6'>
            <div className='w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-xl'>
              <svg
                  className='w-10 h-10 text-white'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z'
                />
              </svg>
            </div>
            <div className='space-y-2'>
              <h2 className='text-4xl font-bold tracking-tight'>
                { t(AUTH_KEYS.changePassword.leftTitle) }
              </h2>
              <p className='text-emerald-50/80 text-lg max-w-xs mx-auto'>
                { t(AUTH_KEYS.changePassword.leftSubtitle) }
              </p>
            </div>
          </div>
        }
    >
      <div className='flex items-start flex-col w-full gap-5 pt-2'>
        <CompanyLogoBadge
            logoUrl={companyLogo}
            companyName={companyName}
            altText='HiAcuity Logo'
        />
        <div className='flex flex-col gap-4'>
          <h1 className='text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight'>
            { t(AUTH_KEYS.changePassword.title) }
          </h1>
          <p className='text-sm text-slate-500'>
            { t(AUTH_KEYS.changePassword.subtitle) }
          </p>
        </div>
      </div>
      <form
          className='flex flex-col space-y-2 mt-10 pb-20'
          onSubmit={handleSubmit}
          data-testid='change-password-form'
      >
        <TextInput
            type='password'
            label={t(AUTH_KEYS.changePassword.password)}
            data-testid='password'
            name='password'
            value={password.password}
            onChange={handleChange}
        />
        <span className={styles.invalidInput}>
          &nbsp;{ !formValid ? password.errors.passwordError : '' }
        </span>
        <TextInput
            type='password'
            label={t(AUTH_KEYS.changePassword.confirmPassword)}
            data-testid='confirmPassword'
            name='confirmPassword'
            value={password.confirmPassword || ''}
            onChange={handleChange}
        />
        <span className={styles.invalidInput}>
          &nbsp;{ !formValid ? password.errors.confirmPasswordError : '' }
        </span>
        <div className='flex md:flex-row flex-col items-center gap-2 w-full'>
          <Button
              onClick={() => history.goBack()}
              label={t(AUTH_KEYS.changePassword.cancel)}
              variant='secondary'
              type='button'
          />
          <Button
              onClick={() => {}}
              label={t(AUTH_KEYS.changePassword.changeButton)}
              variant='primary'
              type='submit'
          />
        </div>
      </form>
    </AuthenticationUiLayout>
  );
};
